/**
 * The box in front of an order's row, and the one over the column.
 *
 * `relative z-[1]` on both, for the reason the call queue's boxes give: every list in this
 * CRM stretches the code's button over the whole row (`::after`), and a box under it would
 * open the order instead of ticking it.
 *
 * Both sit at the start of the first cell (the date), not in a column of their own: a
 * phone folds a row into a card whose top line is that first cell and the row's file
 * button (`crm-list-stack`), and a box of its own would push the date off that line.
 *
 * A row that may not go gets a greyed box rather than none, and the box says why on hover:
 * a gap where every other row has a box reads as a fault.
 */
import { Checkbox } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { pagePicks, whyKept, type PickableRow } from "./lib";
import type { OrderPicks } from "./useOrderPicks";

export function OrderPick({ picks, row, paid }: { picks: OrderPicks; row: PickableRow & { code: string }; paid: boolean }) {
  const t = useT();
  const kept = row.removable ? null : whyKept(paid);
  return (
    // The hint sits on the wrapper: a disabled box takes no pointer, so its own title would never show
    <span className="relative z-[1] flex flex-none items-center" title={kept ? t(`sales.orderPicks.kept.${kept}`) : undefined}>
      <Checkbox
        checked={picks.has(row.id)}
        disabled={!row.removable}
        onCheckedChange={() => picks.toggle(row.id)}
        aria-label={t("sales.orderPicks.pick", { code: row.code })}
      />
    </span>
  );
}

export function OrderPickAll({ picks, rows }: { picks: OrderPicks; rows: readonly PickableRow[] }) {
  const t = useT();
  const page = pagePicks(rows, picks.picked);
  return (
    <span className="relative z-[1] flex flex-none items-center">
      <Checkbox
        // "Some" is a state of its own: three ticked of twenty must not look like none
        checked={page.some ? "indeterminate" : page.all}
        disabled={page.ids.length === 0}
        onCheckedChange={() => picks.setMany(page.ids, !page.all)}
        aria-label={t("sales.orderPicks.pickPage")}
      />
    </span>
  );
}
